import { useMutation, useQuery } from "@tanstack/react-query";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useCustomerValue } from "providers/CustomerContext";
import { useState } from "react";
import { Link } from "react-router-dom";
import { getInventorySKUList } from "services/data.inventory.service";
import { Success, Error_, LoadingDialog } from "ui-components/Alert";
import { TableCard, ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import BasicTabs from "ui-components/tabs/BasicTabs";
import { inventoryColumns } from "./columns";
import { SyncButton } from "ui-components/IconButton";
import { inventorySync } from "services/data.inventory.service";
import { useTeamValue } from "providers/TeamContext";
import { CSVLink } from "react-csv";
import { getFileName } from "layouts/views/common/file";
import dayjs from "dayjs";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import { FormControl, FormLabel, Grid, TextField } from "@mui/material";

const tabs = ["All", "Inactive"];

const headers = [
  { label: "SKU Code", key: "skuCode" },
  { label: "Barcode", key: "barcode" },
  { label: "Description", key: "description" },
  { label: "Warehouse Qty", key: "quantity" },
  { label: "Hold Qty", key: "holdQty" },
  { label: "Committed Qty", key: "committedQty" },
  { label: "Allocated Qty", key: "allocatedQty" },
  { label: "Available Qty", key: "availableQty" },
  { label: "Zone Id", key: "zoneId" },
  { label: "Zone Type", key: "zoneType" },
];

function InventoryList() {
  const { customerCode } = useCustomerValue();
  const { permissions, allowAll } = useTeamValue();
  const { downloadCsv, isDownloading } = useCsvDownloadHook();

  const addPermission =
    allowAll ||
    (Boolean(permissions["Inventory SKU List"]) &&
      permissions["Inventory SKU List"].findIndex((b) => b === "new") !== -1);
  const syncPermission = 
    (Boolean(permissions["Team Role"]) && 
    permissions["Team Role"].some(a => a === "access"));
  
  const columns = [
    {
      header: "SKU Code",
      accessorKey: "skuCode",
      width: "200px",
      cell: ({ row }) => {
        return parseInt(row.original.quantity) > 0 ? (
          <Link
            style={{ color: "#1A73E8", fontWeight: "bold" }}
            to="/inventory/sku/details"
            state={{ customerCode, skuCode: row.original.skuCode, zoneID: row.original.zoneID }}
          >
            <span style={{ textDecoration: "underline" }}>{row.original.skuCode}</span>
          </Link>
        ) : (
          <span>{row.original.skuCode}</span>
        );
      },
    },
    ...inventoryColumns,
    {
      header: "Action",
      accessorKey: "action",
      width: "100px",
      enableColumnFilter: false,
      cell: ({ row }) => {
        if (row.original.zoneType === "Storage" || row.original.zoneType === "Quarantine") {
          return (
            <SyncButton
              title="sync inventory"
              disabled={!addPermission}
              onClick={() => {
                syncMutation.mutate({ customerCode, skuCodes: [row.original.skuCode] });
              }}
            />
          );
        }
      },
    },
  ];

  const [status, setStatus] = useState("All");
  const [kitType, setKitType] = useState("SINGLE");
  const [skuCodes, setSKUCodes] = useState("");
  const [alert, setAlert] = useState(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [list, setList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const { isFetching, refetch } = useQuery({
    queryKey: ["inventory-list", { customerCode, status, kitType, skuCodes }],
    queryFn: () => getInventorySKUList({ customerCode, status, kitType, skuCodes }),
    onSuccess: (data) => {
      setList(data.data.data.map((x) => ({ ...x, quantity: x.quantity.toString() })));
      setFilteredData(data.data.data.map((x) => ({ ...x, quantity: x.quantity.toString() })));
    },
    enabled: false,
    onError,
  });
  const syncMutation = useMutation(inventorySync, {
    onSuccess: (data) => {
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });
  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };
  const tableProps = {
    data: list,
    columns,
    onFilterRowsChange: handleFilterRowsChange,
  };
  const syncStatus = syncMutation.status === "loading";
  return (
    <ListLayout>
      {(isFetching || syncStatus) && <LoadingDialog />}
      {isDownloading && <ExportDialog />}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox margin="10px">
          <Grid container spacing={3}>
            <Grid item xs={10} sx={{ marginRight: "20px"}}>
              <TextField
                label="Search SKU Code (split by comma ,) - Max 10"
                fullWidth
                value={skuCodes}
                onChange={(e) => setSKUCodes(e.target.value)}
              />
            </Grid>
            <Grid item xs={1}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  refetch();
                }}
              >
                search
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" margin="10px">
          <MDBox>
            <MDButton
              sx={{ borderRadius: "5px" }}
              variant={kitType === "SINGLE" ? "gradient" : "outlined"}
              color="info"
              size="small"
              onClick={() => setKitType("SINGLE")}
            >
              Product
            </MDButton>
            <MDButton
              sx={{ marginLeft: "1rem", borderRadius: "5px" }}
              variant={kitType === "KIT" ? "gradient" : "outlined"}
              color="info"
              size="small"
              onClick={() => setKitType("KIT")}
            >
              Kit
            </MDButton>
            <FormControl>
              <FormLabel
                sx={{ color: "red", fontWeight: "bold", fontSize: "1rem", marginTop: ".5rem" }}
              >
                * Click search button to load data, empty filter will load all SKUs
              </FormLabel>
            </FormControl>
          </MDBox>
          <MDBox>
            {/* <MDButton
              sx={{ borderRadius: "5px" }}
              variant={kitType === "SINGLE" ? "gradient" : "outlined"}
              color="info"
              size="small"
              disabled={!syncPermission}
              onClick={() => syncMutation.mutate({ customerCode, skuCodes: [] })}
            >
              Sync All
            </MDButton> */}

            {/* <CSVLink
              headers={headers}
              filename={getFileName("Inventory", customerCode)}
              data={filteredData.map((x) => ({ ...x, skuCode: " " + x.skuCode }))}
            >
              <MDButton
                sx={{ marginLeft: "1rem", borderRadius: "5px" }}
                variant={kitType === "SINGLE" ? "gradient" : "outlined"}
                color="info"
                size="small"
                disabled={!addPermission}
              >
                Export
              </MDButton>
            </CSVLink> */}
            <MDButton
              sx={{ marginLeft: "1rem", borderRadius: "5px" }}
              variant={kitType === "SINGLE" ? "gradient" : "outlined"}
              color="info"
              size="small"
              disabled={!addPermission}
              onClick={() => {
                const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_Inventory`;
                downloadCsv(filename, filteredData, headers)
              }}
            >
              export
            </MDButton>
          </MDBox>
        </MDBox>
        <BasicTable data={list} columns={columns} onFilterRowsChange={handleFilterRowsChange} />
      </TableCard>
    </ListLayout>
  );
}

export default InventoryList;
